import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ActionQueuesPanel } from '../components/action-queues';
import { PageHeader } from '../components/layout';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { api } from '../lib/api';
import { can, useMe } from '../lib/session';
import type { MatchQueueItem, PriceApproval, SupplierRow } from '../lib/types';

function Tile({ title, value, to }: { title: string; value: number | undefined; to: string }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader title={title} />
      <CardContent className="flex items-end justify-between">
        <span className="text-3xl font-semibold">{value ?? '…'}</span>
        <Link to={to} className="text-sm text-primary underline-offset-4 hover:underline">
          {t('dashboard.open')}
        </Link>
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const me = useMe();
  const approvals = useQuery({
    queryKey: ['pricing', 'approvals'],
    queryFn: () => api<{ items: PriceApproval[] }>('/pricing/approvals'),
    enabled: can(me, 'pricing.approve'),
  });
  const queue = useQuery({
    queryKey: ['suppliers', 'match-queue'],
    queryFn: () => api<{ items: MatchQueueItem[] }>('/suppliers/match-queue'),
    enabled: can(me, 'imports.run'),
  });
  const suppliers = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api<{ items: SupplierRow[] }>('/suppliers'),
    enabled: can(me, 'suppliers.read'),
  });

  return (
    <>
      <PageHeader
        title={t('dashboard.title')}
        description={t('dashboard.hello', { name: me.displayName ?? me.email })}
      />
      <ActionQueuesPanel />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {can(me, 'pricing.approve') ? (
          <Tile
            title={t('dashboard.pendingApprovals')}
            value={approvals.data?.items.length}
            to="/price-approvals"
          />
        ) : null}
        {can(me, 'imports.run') ? (
          <Tile
            title={t('dashboard.matchQueue')}
            value={queue.data?.items.length}
            to="/match-queue"
          />
        ) : null}
        {can(me, 'suppliers.read') ? (
          <Tile
            title={t('dashboard.suppliers')}
            value={suppliers.data?.items.length}
            to="/suppliers"
          />
        ) : null}
      </div>
    </>
  );
}
